import { type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { parseApiError } from '@saas-kit/lib';
import {
  Checkbox,
  Drawer,
  ErrorBanner,
  InputField,
  SubmitButton,
  Textarea,
  toast,
} from '@saas-kit/ui';

import { ANNOUNCEMENT_CHANNELS, type CreateAnnouncementPayload } from './api';
import { useAnnouncementForm } from './useAnnouncementForm';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isPending: boolean;
  onSubmit: (payload: CreateAnnouncementPayload) => Promise<void>;
}

const AUDIENCE_OPTIONS = ['all', 'specific'] as const;

export function AnnouncementComposeForm({
  open,
  onOpenChange,
  isPending,
  onSubmit,
}: Props): JSX.Element {
  const { t } = useTranslation('adminAnnouncements');
  const form = useAnnouncementForm(t);

  function handleClose(next: boolean): void {
    if (!next) form.reset();
    onOpenChange(next);
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    form.setError(null);
    const { payload, error } = form.buildPayload();
    if (!payload) {
      form.setError(error ?? null);
      return;
    }
    void (async () => {
      try {
        await onSubmit(payload);
        toast.success(t('toasts.created'));
        form.reset();
        onOpenChange(false);
      } catch (err) {
        form.setError(parseApiError(err).message);
      }
    })();
  }

  return (
    <Drawer
      open={open}
      onOpenChange={handleClose}
      title={t('compose.title')}
      description={t('compose.description')}
      width="lg"
    >
      <form className="space-y-4" onSubmit={handleSubmit}>
        <InputField
          label={t('compose.subject_label')}
          value={form.subject}
          onChange={(e) => form.setSubject(e.target.value)}
          maxLength={255}
          autoFocus
          required
        />

        <div className="space-y-1">
          <label
            htmlFor="announcement-body"
            className="text-sm font-medium text-foreground"
          >
            {t('compose.body_label')}
          </label>
          <Textarea
            id="announcement-body"
            value={form.body}
            onChange={(e) => form.setBody(e.target.value)}
            rows={6}
            maxLength={10000}
            required
          />
        </div>

        <fieldset className="space-y-2">
          <legend className="text-sm font-medium text-foreground">
            {t('compose.audience_label')}
          </legend>
          <div className="flex gap-4 text-sm">
            {AUDIENCE_OPTIONS.map((option) => (
              <label key={option} className="flex items-center gap-2">
                <input
                  type="radio"
                  name="audience"
                  value={option}
                  checked={form.audience === option}
                  onChange={() => form.setAudience(option)}
                />
                {t(`audience.${option}`)}
              </label>
            ))}
          </div>
        </fieldset>

        {form.audience === 'specific' ? (
          <div className="space-y-1">
            <label
              htmlFor="announcement-tenants"
              className="text-sm font-medium text-foreground"
            >
              {t('compose.tenants_label')}
            </label>
            <Textarea
              id="announcement-tenants"
              value={form.tenantIds}
              onChange={(e) => form.setTenantIds(e.target.value)}
              rows={3}
              placeholder={t('compose.tenants_placeholder')}
            />
            <p className="text-xs text-muted-foreground">
              {t('compose.tenants_hint')}
            </p>
          </div>
        ) : null}

        <fieldset className="space-y-2">
          <legend className="text-sm font-medium text-foreground">
            {t('compose.channels_label')}
          </legend>
          <div className="flex gap-4 text-sm">
            {ANNOUNCEMENT_CHANNELS.map((channel) => (
              <label key={channel} className="flex items-center gap-2">
                <Checkbox
                  checked={form.channels.includes(channel)}
                  onChange={() => form.toggleChannel(channel)}
                />
                {t(`channels.${channel}`)}
              </label>
            ))}
          </div>
        </fieldset>

        {form.error ? <ErrorBanner>{form.error}</ErrorBanner> : null}

        <div className="pt-2">
          <SubmitButton
            loading={isPending}
            label={t('compose.submit')}
            loadingLabel={t('compose.submitting')}
          />
        </div>
      </form>
    </Drawer>
  );
}
